import React from 'react';
import PropTypes from 'prop-types';
import reducer, { INITIAL_STATE } from './reducer';
import {
  normalizeImageMode,
  findImageSettingByType,
} from '../../utils/imageUtils';
import { CONTENT_TYPES, IMAGE_MODE } from '../../constants/contentTypes';
import { contentItemsToShowInCarousel } from './utils';
import { DISABLED_SKELETON_LIST_SOURCES, KID_URLS_SECTIONS } from './constants';
import * as hooks from './hooks';
import { FatalErrorBoundary } from '../WithErrorBoundary';
import { selectOrderType } from './actions';

/**
 * HOC component provides unity component data for tbx-widgets rendering.
 *
 * @param {*} {
 *   renderWidget,
 *   customEmptyComponent = null,
 *   customLoadingComponent = null,
 *   sectionID,
 *   tokenData,
 *   widgetID
 * }
 * @returns Component
 */
function UnityComponentProvider({
  translationTexts,
  renderWidget,
  customEmptyComponent = null,
  customLoadingComponent = null,
  tokenData,
  appSettings,
  networks,
  channels,
  dispatchRedux,
  authenticated,
  widgetData = {
    componentParameters: {},
  },
  noImageDefault,
  fetchFavoriteContentByProfile,
  sections,
  favorites,
}) {
  const [state, dispatch] = React.useReducer(reducer, {
    ...INITIAL_STATE,
  });
  const { componentItems, orderTypeSelected } = state;
  const { page } = componentItems || {};
  const { componentType, componentParameters } = widgetData || {};
  const {
    title,
    textMode,
    contentListDisplayItems,
    maxItems,
    sectionLinked,
    listSource,
    rowType,
    itemLabel,
    itemStyle,
  } = componentParameters || {};

  hooks.useDidMount({
    tokenData,
    widgetData,
    state,
    dispatch,
    dispatchRedux,
    authenticated,
    fetchFavoriteContentByProfile,
  });
  hooks.useRefetchFavorites({
    tokenData,
    widgetData,
    state,
    dispatch,
    dispatchRedux,
    authenticated,
    favorites,
  });

  const imageMode =
    componentType === 'gallery_wall' ? 'POSTER' : normalizeImageMode(rowType);
  const { w, h } = findImageSettingByType(appSettings.imageSizes, imageMode);

  const contentNoImageProps = {
    width: w || noImageDefault(imageMode).width,
    height: h || noImageDefault(imageMode).height,
    backgroundColorOne: noImageDefault().backgroundColorOne,
    backgroundColorTwo: noImageDefault().backgroundColorTwo,
  };

  const selectFilterHandler = (orderType) =>
    dispatch(selectOrderType(orderType));

  const loadMoreDataInComponent = () => {
    hooks.fetchComponentData({
      tokenData,
      widgetData,
      state,
      dispatch,
      dispatchRedux,
      authenticated,
      isLoadMoreDataInComponent: true,
      fetchFavoriteContentByProfile,
    });
  };

  if (state.isFetchingData && customLoadingComponent) {
    if (DISABLED_SKELETON_LIST_SOURCES.includes(listSource)) {
      return customLoadingComponent(null);
    } else {
      let loadingObjectResult;
      const countItem = contentItemsToShowInCarousel(
        maxItems,
        contentListDisplayItems,
      );

      if (componentType === 'carousel') {
        loadingObjectResult = {
          isLoading: true,
          countItem,
          title,
          textMode,
          imageMode,
          itemLabel,
          contentNoImageProps,
          sectionLinked,
        };
        return customLoadingComponent(loadingObjectResult);
      }

      if (componentType === 'gallery_wall') {
        loadingObjectResult = {
          isLoading: true,
          countItem,
          title,
          textMode,
          imageMode,
          itemLabel,
          contentNoImageProps,
          sectionLinked,
          enableFilters: true,
          customClassName: 'tbxContentGrid__gallery_wall_filters',
        };

        return customLoadingComponent(loadingObjectResult);
      }

      if (componentType === 'banner') {
        loadingObjectResult = { isLoading: true };
        return customLoadingComponent(loadingObjectResult);
      }

      if (componentType === 'html') {
        return customLoadingComponent(componentParameters);
      }
    }
  }

  if (
    !state.isFetchingData &&
    componentItems.itemCollection.length === 0 &&
    componentType !== 'gallery_wall' &&
    componentType !== 'html' &&
    componentType !== 'section_nav'
  ) {
    console.warn(
      `>>> Component "${componentType}" ID:"${widgetData.id}" is empty`,
    );
    return customEmptyComponent;
  }

  const widgetProps = (({
    textMode = 'none',
    title,
    rowType,
    sectionLinked,
    maxItems,
    contentListDisplayItems,
    itemLabel,
  }) => {
    if (
      componentType === 'banner' ||
      componentType === 'carousel' ||
      componentType === 'gallery_wall'
    ) {
      return {
        translationTexts,
        contentCardProps: {
          prefixNetworkText: `${translationTexts?.available_in}: `,
        },
        contentCollection: ['gallery_wall', 'carousel'].includes(componentType)
          ? componentItems?.itemCollection.map((item) =>
              item?.content
                ? {
                    ...item.content,
                    bookmark: item.bookmark,
                    serieProps: item.serieProps,
                  }
                : item,
            )
          : componentItems?.itemCollection
              .slice(
                0,
                contentItemsToShowInCarousel(maxItems, contentListDisplayItems),
              )
              .map((item) => {
                switch (widgetData.componentType) {
                  case 'banner':
                    const section = sections?.find(
                      (section) => section.url === item.sectionUrl,
                    );

                    // Rating 0 for kids sections only
                    const sectionRating =
                      section &&
                      KID_URLS_SECTIONS.includes(section?.url?.toLowerCase())
                        ? 0
                        : 2;

                    // Rating according to the content type
                    const ratingLevel =
                      item?.relatedContent?.ratingLevel ||
                      (item.sectionUrl && sectionRating) ||
                      0;

                    return {
                      ratingLevel,
                      sectionName: section?.name,
                      ...item,
                    };

                  default:
                    return item?.content
                      ? {
                          ...item.content,
                          bookmark: item.bookmark,
                          serieProps: item.serieProps,
                        }
                      : item;
                }
              }),
        contentNoImageProps,
        networks,
        channels,
        listSource,
        playAndFavorite:
          normalizeImageMode(rowType) === IMAGE_MODE.POSTER ||
          normalizeImageMode(rowType) === IMAGE_MODE.SIMPLE_LARGE,
        sectionLinked,
        imageMode,
        trailerOverview:
          (normalizeImageMode(rowType) === IMAGE_MODE.SIMPLE_LARGE ||
            normalizeImageMode(rowType) === IMAGE_MODE.THUMB) &&
          componentItems?.itemCollection.every(
            (prop) => prop?.content?.contentType !== CONTENT_TYPES.BROADCAST,
          ),
        ...{ title },
        ...{ textMode },
        itemLabel,
        itemStyle,
        enableFilters: componentType === 'gallery_wall',
        orderTypeSelected,
        selectFilterHandler,
        loadMoreDataInComponent,
        customClassName:
          componentType === 'gallery_wall'
            ? 'tbxContentGrid__gallery_wall_filters'
            : '',
        page,
        noItemsDescription:
          componentItems?.itemCollection.length === 0
            ? translationTexts?.['at_this_time_there_are_no_content_available.']
            : '',
        contentListDisplayItems,
      };
    }

    if (componentType === 'epg_grid') {
      const isEpgPlayer = true;
      return {
        content: componentItems.itemCollection.map((e) => e.content),
        unityToken: tokenData,
        contentNoImageProps,
        player: isEpgPlayer,
        epgPage: page,
        loadMoreDataInComponent,
        epgUrl: (componentItems && componentItems.epgUrl) || null,
      };
    }

    if (componentType === 'html') {
      return componentParameters;
    }
  })(componentParameters);

  return renderWidget(widgetProps);
}

UnityComponentProvider.propTypes = {
  renderWidget: PropTypes.func.isRequired,
  EmptyComponent: PropTypes.element,
  LoadingComponent: PropTypes.element,
  tokenData: PropTypes.shape({
    auth_type: PropTypes.string,
    access_token: PropTypes.string,
  }).isRequired,
  appSettings: PropTypes.object,
  networks: PropTypes.array,
  channels: PropTypes.array,
  widgetData: PropTypes.shape({
    componentParameters: PropTypes.object,
    id: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
  }).isRequired,
  noImageDefault: PropTypes.object,
  fetchFavoriteContentByProfile: PropTypes.func.isRequired,
};

export default FatalErrorBoundary(UnityComponentProvider);
